import { Navigate, useLocation } from 'react-router';
import { motion } from 'motion/react';
import { AlertCircle, RefreshCw, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LoadingPage } from '@/components/common';
import { Button } from '@/components/ui/button';
import { useOnboarding } from '@/hooks/useChild';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * Check if error is a network error (cannot connect to server)
 */
function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('network') ||
    msg.includes('timeout') ||
    msg.includes('econnrefused') ||
    msg.includes('failed to fetch') ||
    msg.includes('net::err')
  );
}

/**
 * OnboardingGuard - Child profile guard component
 *
 * Checks if user has created a child profile:
 * - Shows Loading while checking
 * - Shows error prompt + retry on API error (not maintenance page)
 * - Redirects to onboarding page if no child profile
 * - Renders children if child profile exists
 *
 * Note:
 * - Should be used after AuthGuard to ensure user is logged in
 * - API errors don't redirect to maintenance page because health check passed
 * - Business API errors should show specific error, not "service maintenance"
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const queryClient = useQueryClient();
  const { needsOnboarding, isLoading, error } = useOnboarding();
  const [isRetrying, setIsRetrying] = useState(false);

  /**
   * Retry callback: refetch child profile
   */
  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    await queryClient.invalidateQueries({ queryKey: ['children'] });
    // Delay reset to show loading effect
    setTimeout(() => setIsRetrying(false), 500);
  }, [queryClient]);

  // Still loading child profile status
  if (isLoading) {
    return <LoadingPage message={t('loading.loadingUser')} />;
  }

  // API error: show error prompt + retry button
  // Note: Don't redirect to maintenance page because:
  // 1. Health check passed, server is available
  // 2. This error is a business API error (could be bug, permissions, etc.)
  // 3. Should show specific error message, not "service maintenance"
  if (error) {
    const isNetwork = isNetworkError(error);
    const Icon = isNetwork ? WifiOff : AlertCircle;
    const title = isNetwork ? t('guard.networkError') : t('guard.loadError');
    const description = isNetwork ? t('guard.networkErrorDesc') : t('guard.loadErrorDesc');

    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm text-center"
        >
          {/* Icon */}
          <motion.div
            className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full ${
              isNetwork ? 'bg-slate-100' : 'bg-amber-50'
            }`}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Icon
              className={`h-8 w-8 ${isNetwork ? 'text-slate-500' : 'text-amber-500'}`}
            />
          </motion.div>

          {/* Title */}
          <h2 className="mb-2 text-lg font-semibold text-slate-800">{title}</h2>

          {/* Description */}
          <p className="mb-6 text-sm text-slate-500">{description}</p>

          {/* Retry Button */}
          <Button
            onClick={handleRetry}
            disabled={isRetrying}
            variant="outline"
            className="w-full"
          >
            {isRetrying ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                </motion.div>
                {t('loading.retrying')}
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('errors.tryAgain')}
              </>
            )}
          </Button>
        </motion.div>
      </div>
    );
  }

  // Need to create child profile, redirect to onboarding
  if (needsOnboarding) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  // Has child profile, render children
  return <>{children}</>;
}
